/**
 * Wallet discovery + SIWE driver.
 *
 * Modern wallets announce themselves via EIP-6963 instead of (or in addition
 * to) injecting `window.ethereum`. We listen for the announce event AND fall
 * back to the legacy global — covers MetaMask, Brave Wallet, Rabby, Phantom,
 * Coinbase Wallet, and anything else that follows either standard.
 *
 * Signing goes through viem's walletClient — viem normalizes the EIP-1193
 * `personal_sign` call across wallet quirks (hex vs UTF-8 encoding, param
 * order, mobile in-app browser SDK differences). We hand-format the SIWE
 * message string ourselves so we never depend on the `siwe` package's Node
 * Buffer shim (which Vite cannot externalize cleanly for the browser).
 */
import { createWalletClient, custom, getAddress, type WalletClient, type Address } from 'viem';
import { mainnet } from 'viem/chains';

/**
 * Normalize a wallet-supplied address to an EIP-55 CHECKSUMMED 0x address.
 *
 * Critical for SIWE: the `siwe` parser validates the address checksum and
 * rejects the ENTIRE message ("message_parse_failed") if it isn't checksummed.
 * Injected wallets (MetaMask) hand back a checksummed address so they work, but
 * WalletConnect returns a LOWERCASE address (and sometimes a CAIP-10 id like
 * `eip155:1:0x…`), which made desktop QR sign-in fail with "Sign-in payload
 * rejected by the server". Strip any CAIP prefix, then re-checksum.
 */
function toChecksumAddress(raw: string): Address {
  const hex = raw.includes(':') ? (raw.split(':').pop() ?? raw) : raw;
  return getAddress(hex.trim());
}

interface EthProvider {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
}

interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
}

interface EIP6963ProviderDetail {
  info: EIP6963ProviderInfo;
  provider: EthProvider;
}

declare global {
  interface Window {
    ethereum?: EthProvider;
  }
  interface WindowEventMap {
    'eip6963:announceProvider': CustomEvent<EIP6963ProviderDetail>;
  }
}

const discovered: EIP6963ProviderDetail[] = [];

if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (event) => {
    const detail = event.detail;
    if (!discovered.find((p) => p.info.uuid === detail.info.uuid)) {
      discovered.push(detail);
    }
  });
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

function rescanForProviders(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

function pickProvider(): EthProvider | null {
  if (discovered.length > 0 && discovered[0]) return discovered[0].provider;
  if (typeof window !== 'undefined' && window.ethereum) return window.ethereum;
  return null;
}

/**
 * The EIP-1193 provider the user is actually signed in with — the live
 * WalletConnect session if one exists, otherwise the injected wallet. Used to
 * build transactions (e.g. the USDC payment) on the same wallet they connected.
 */
export function getActiveProvider(): EthProvider | null {
  if (wcProvider) return wcProvider;
  rescanForProviders();
  return pickProvider();
}

/**
 * Ensure there's a LIVE provider to build a transaction with.
 *
 * Being "signed in" only means a valid session token in localStorage — the
 * in-memory EIP-1193 provider does NOT survive a page reload. So before a
 * payment we may have an authenticated user but no wallet object to sign with
 * (classic on mobile Safari, where sign-in went through WalletConnect and the
 * page was since reloaded). This re-establishes the link: an injected wallet is
 * already a live object; a WalletConnect session is restored from storage (or,
 * if it has lapsed, re-opened) before we hand it back.
 */
export async function ensurePaymentProvider(): Promise<EthProvider | null> {
  const live = getActiveProvider();
  if (live) return live;
  if (!WC_PROJECT_ID) return null;
  try {
    const p = await initWcProvider();
    await p.enable(); // restores an existing session silently; else opens the QR/redirect
    return p;
  } catch {
    return null;
  }
}

export function hasWallet(): boolean {
  rescanForProviders();
  return pickProvider() !== null;
}

/**
 * Wallet in-app browsers (MetaMask, Trust, Rabby mobile) sometimes inject
 * their provider AFTER page load. Before concluding "no wallet" and falling
 * back to the WalletConnect QR, poll briefly for a late injection so the
 * in-wallet flow is always preferred when one exists.
 */
export async function waitForInjectedWallet(timeoutMs = 1200): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    rescanForProviders();
    if (pickProvider() !== null) return true;
    await new Promise((r) => window.setTimeout(r, 120));
  }
  return pickProvider() !== null;
}

export function detectedWalletNames(): string[] {
  rescanForProviders();
  const names = discovered.map((p) => p.info.name);
  if (typeof window !== 'undefined' && window.ethereum && names.length === 0) {
    names.push('Browser wallet');
  }
  return names;
}

export interface SiweOutcome {
  address: string;
  message: string;
  signature: string;
}

export class SiweUserRejected extends Error {
  constructor() {
    super('user_rejected');
    this.name = 'SiweUserRejected';
  }
}

/** Detailed error carrying the original error's serializable shape for UI diagnostics. */
export class SiweDiagnosticError extends Error {
  constructor(
    message: string,
    public readonly stage:
      | 'no_wallet'
      | 'eth_request_accounts'
      | 'no_account'
      | 'create_client'
      | 'sign_message'
      | 'unknown',
    public readonly detail: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'SiweDiagnosticError';
  }
}

export async function signInWithEthereum(nonce: string): Promise<SiweOutcome> {
  rescanForProviders();
  const provider = pickProvider();
  if (!provider) {
    throw new SiweDiagnosticError('No wallet provider detected.', 'no_wallet', {
      hasWindowEthereum: typeof window !== 'undefined' && !!window.ethereum,
      eip6963Count: discovered.length,
      wallets: discovered.map((p) => p.info.name),
    });
  }

  // 1. Request accounts.
  let accounts: string[];
  try {
    accounts = (await provider.request({ method: 'eth_requestAccounts' })) as string[];
  } catch (e) {
    if (isUserRejection(e)) throw new SiweUserRejected();
    throw new SiweDiagnosticError(
      describe(e) || 'eth_requestAccounts failed',
      'eth_request_accounts',
      serialize(e),
    );
  }
  const rawAddress = accounts[0];
  if (!rawAddress) {
    throw new SiweDiagnosticError('Wallet returned no accounts.', 'no_account', { accounts });
  }

  return signSiweWithProvider(provider, rawAddress, nonce);
}

/* ---------------------------------------------------------------------- */
/* WalletConnect — QR sign-in for desktop visitors whose wallet lives on   */
/* their phone. Heavy SDK, so it is dynamically imported only on use; the  */
/* relay needs a (free) WalletConnect Cloud project id at build time.      */
/* ---------------------------------------------------------------------- */

const WC_PROJECT_ID =
  (import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as string | undefined) ?? '';

/** True when the build carries a WalletConnect project id. */
export function walletConnectAvailable(): boolean {
  return WC_PROJECT_ID.length > 0;
}

interface WcProvider extends EthProvider {
  enable(): Promise<string[]>;
  disconnect(): Promise<void>;
}

/** The slice of the WalletConnect EthereumProvider we read to align its chain. */
interface WcInternals {
  chainId?: number;
  setDefaultChain?: (chainId: number, rpcUrl?: string) => void;
  session?: {
    namespaces?: Record<string, { accounts?: string[]; chains?: string[] }>;
  };
}

/**
 * Align the WalletConnect provider's default chain to a chain the wallet
 * actually approved.
 *
 * `personal_sign` is routed by the provider through its default `chainId`. If
 * that chain isn't in the approved session — which happens when a mobile wallet
 * approves the connection on its *current* network rather than the mainnet we
 * requested — WalletConnect throws "Missing or invalid. request() chainId:
 * eip155:1" at the signature step. We read the session's approved eip155 chains
 * and, only on a mismatch, point the provider at one of them. A SIWE signature
 * is chain-agnostic (the server recovers the signer from the message text), so
 * which approved chain we sign on doesn't matter.
 */
function alignWcDefaultChain(provider: WcProvider): void {
  try {
    const p = provider as unknown as WcInternals;
    const ns = p.session?.namespaces?.['eip155'];
    const fromAccounts = (ns?.accounts ?? []).map((a) =>
      a.split(':').slice(0, 2).join(':'),
    );
    const approved = [...(ns?.chains ?? []), ...fromAccounts];
    const ids = approved
      .map((c) => Number(c.split(':')[1]))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length && (p.chainId == null || !ids.includes(p.chainId))) {
      p.setDefaultChain?.(ids[0] as number);
    }
  } catch {
    /* best-effort — if anything is shaped unexpectedly, let signing proceed */
  }
}

let wcProvider: WcProvider | null = null;

/**
 * Lazily create (once) the WalletConnect provider. Mainnet (1) is the only
 * required chain — it's what SIWE signs against.
 *
 * NOTE: do NOT add `optionalChains` here. Declaring Base (8453) as an optional
 * chain re-shapes the session namespaces such that some wallets approve the
 * session without a usable default chain, and the subsequent `personal_sign`
 * fails with "Missing or invalid. request() chainId: eip155:1". Sign-in is the
 * critical path, so we keep the namespace to mainnet only; the USDC payment's
 * `wallet_switchEthereumChain` to Base still works on most wallets without it.
 */
async function initWcProvider(): Promise<WcProvider> {
  if (wcProvider) return wcProvider;
  const { EthereumProvider } = await import('@walletconnect/ethereum-provider');
  wcProvider = await EthereumProvider.init({
    projectId: WC_PROJECT_ID,
    chains: [1],
    showQrModal: true,
    // Declare the session methods EXPLICITLY, and keep the REQUIRED set to the one
    // method sign-in actually needs: `personal_sign`. The provider's default also
    // requires `eth_sendTransaction`; some mobile wallets hesitate to approve a
    // mainnet send-tx capability and either reject the session or quietly drop the
    // later `personal_sign` request — which is exactly the "wallet connects but the
    // sign prompt never appears" symptom. Everything beyond signing (the USDC
    // payment's send-tx + chain switch) is OPTIONAL, so the wallet still grants it
    // when it can, without it ever gating the critical sign-in path.
    methods: ['personal_sign'],
    optionalMethods: [
      'eth_sendTransaction',
      'eth_signTypedData_v4',
      'eth_sign',
      'wallet_switchEthereumChain',
      'wallet_addEthereumChain',
    ],
    events: ['chainChanged', 'accountsChanged'],
    optionalEvents: ['chainChanged', 'accountsChanged'],
    metadata: {
      name: 'Gilding',
      description: 'A prompt amplifier. Bring a sketch, leave with gold.',
      url: window.location.origin,
      icons: [],
    },
  });
  return wcProvider;
}

/**
 * WalletConnect sign-in. The nonce is fetched via `getNonce` AFTER the phone
 * approves the session — scanning the QR, opening the wallet, and approving
 * the connection can take 20-40s, so issuing the nonce up front would burn
 * its short server TTL before the user ever reaches the signature step.
 */
export async function signInWithWalletConnect(
  getNonce: () => Promise<string>,
  opts?: { onAwaitingSignature?: () => void },
): Promise<SiweOutcome> {
  if (!WC_PROJECT_ID) {
    throw new SiweDiagnosticError('WalletConnect is not configured.', 'no_wallet', {});
  }
  let provider: WcProvider;
  try {
    provider = await initWcProvider();
  } catch (e) {
    throw new SiweDiagnosticError(
      describe(e) || 'Could not start WalletConnect',
      'create_client',
      serialize(e),
    );
  }

  // Always begin sign-in from a clean session. WalletConnect persists sessions
  // in localStorage, so a stranded session from a previous attempt gets
  // silently restored — enable() then resolves instantly (no modal) and signing
  // fails with "Missing or invalid request() chainId", because the stale
  // session is stuck on a chain it won't sign on. Tearing it down forces a
  // fresh QR/deep-link connection that signs cleanly.
  try {
    if ((provider as unknown as WcInternals).session) {
      await provider.disconnect();
    }
  } catch {
    /* nothing live to clear */
  }

  // Opens the QR modal; resolves once a phone wallet approves the session.
  let accounts: string[];
  try {
    accounts = await provider.enable();
  } catch (e) {
    if (isUserRejection(e)) throw new SiweUserRejected();
    throw new SiweDiagnosticError(
      describe(e) || 'WalletConnect session failed',
      'eth_request_accounts',
      serialize(e),
    );
  }
  const rawAddress = accounts[0];
  if (!rawAddress) {
    throw new SiweDiagnosticError('Wallet returned no accounts.', 'no_account', { accounts });
  }

  // Some wallets approve the session on their current network rather than the
  // requested mainnet; route the signature through a chain they DID approve so
  // personal_sign doesn't fail with "Missing or invalid. request() chainId".
  alignWcDefaultChain(provider);

  // Session is live — NOW mint the nonce so its clock starts here.
  const nonce = await getNonce();

  // The built-in QR modal handles the CONNECTION only — it gives no cue for the
  // signature request that follows. On desktop the personal_sign is relayed
  // silently to the phone, so the visitor (watching the desktop) sees nothing and
  // doesn't know to reopen their wallet. Tell the UI we're now awaiting the
  // signature so it can prompt "approve in your wallet app". A short settle pause
  // also lets the freshly-approved session be fully ready before the first request
  // (sending immediately occasionally drops it on slower relays).
  opts?.onAwaitingSignature?.();
  await new Promise((r) => window.setTimeout(r, 350));
  return signSiweWithProvider(provider, rawAddress, nonce);
}

/** Tear down any live WalletConnect session (call alongside app logout). */
export async function disconnectWalletConnect(): Promise<void> {
  try {
    await wcProvider?.disconnect();
  } catch {
    /* session already gone */
  }
}

/* ---------------------------------------------------------------------- */

async function signSiweWithProvider(
  provider: EthProvider,
  rawAddress: string,
  nonce: string,
): Promise<SiweOutcome> {
  // Build viem WalletClient. Viem handles the personal_sign quirks across
  // wallets — hex encoding, param order, etc.
  let walletClient: WalletClient;
  let address: Address;
  try {
    walletClient = createWalletClient({
      chain: mainnet,
      transport: custom(provider),
    });
    // Checksum the address (see toChecksumAddress) so the SIWE message parses
    // server-side — WalletConnect's lowercase address would otherwise be rejected.
    address = toChecksumAddress(rawAddress);
  } catch (e) {
    throw new SiweDiagnosticError(
      describe(e) || 'Could not build wallet client',
      'create_client',
      serialize(e),
    );
  }

  const now = new Date();
  const expiration = new Date(now.getTime() + 10 * 60 * 1000);
  const message = buildSiweMessage({
    domain: window.location.host,
    address,
    statement: 'Sign in to Gilding.',
    uri: window.location.origin,
    version: '1',
    chainId: 1,
    nonce,
    issuedAt: now.toISOString(),
    expirationTime: expiration.toISOString(),
  });

  // Sign via viem. Viem internally chooses hex vs UTF-8 based on wallet
  // capability and retries appropriately. If everything fails it surfaces
  // a proper viem error with details we can attach to the diagnostic.
  let signature: string;
  try {
    signature = await walletClient.signMessage({ account: address, message });
  } catch (e) {
    if (isUserRejection(e)) throw new SiweUserRejected();
    throw new SiweDiagnosticError(
      describe(e) || 'signMessage failed',
      'sign_message',
      serialize(e),
    );
  }

  // Guard against a provider (notably WalletConnect right after a session
  // connects) resolving with an empty or malformed signature before the user
  // has actually signed. Posting that to the server yields a confusing
  // "payload rejected" — instead surface a clear, retryable signing error.
  if (typeof signature !== 'string' || !/^0x[0-9a-fA-F]+$/.test(signature)) {
    throw new SiweDiagnosticError(
      'No signature was returned. Approve the signature request in your wallet, then try again.',
      'sign_message',
      { signature: String(signature) },
    );
  }

  return { address, message, signature };
}

function isUserRejection(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false;
  const err = e as { code?: number | string; message?: string; name?: string };
  if (err.code === 4001 || err.code === '4001') return true;
  if (err.name === 'UserRejectedRequestError') return true;
  const msg = (err.message ?? '').toLowerCase();
  return (
    msg.includes('user reject') ||
    msg.includes('user denied') ||
    msg.includes('rejected by user')
  );
}

function describe(e: unknown): string {
  if (!e) return '';
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  return JSON.stringify(e);
}

/** Pull every property we can off an unknown error so the UI can show what really threw. */
function serialize(e: unknown): Record<string, unknown> {
  if (!e || typeof e !== 'object') return { value: String(e) };
  const err = e as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of ['name', 'message', 'code', 'shortMessage', 'details', 'cause', 'stack']) {
    if (k in err) out[k] = err[k];
  }
  // Also stringify the whole thing in case the keys above missed something.
  try {
    out['_full'] = JSON.parse(JSON.stringify(err));
  } catch {
    /* skip */
  }
  return out;
}

interface SiweParams {
  domain: string;
  address: string;
  statement: string;
  uri: string;
  version: string;
  chainId: number;
  nonce: string;
  issuedAt: string;
  expirationTime: string;
}

function buildSiweMessage(p: SiweParams): string {
  return [
    `${p.domain} wants you to sign in with your Ethereum account:`,
    p.address,
    '',
    p.statement,
    '',
    `URI: ${p.uri}`,
    `Version: ${p.version}`,
    `Chain ID: ${p.chainId}`,
    `Nonce: ${p.nonce}`,
    `Issued At: ${p.issuedAt}`,
    `Expiration Time: ${p.expirationTime}`,
  ].join('\n');
}
